/**
 * Quickbuy Drawer (Impact / Maestrooo)
 * - Opens drawer when clicking a product card (prevents navigation)
 * - Injects quick-buy section HTML (zoomable image etc.)
 * - Uses /products/<handle>.js for title/vendor/description + price/compare/discount
 * - Preset qty buttons set quantity; quantity input starts empty (not preselected)
 * - Fixes "+/- adds 2" by NOT handling +/- clicks (theme quantity-selector already does)
 * - Adds 2 buttons:
 *    1) In den Warenkorb
 *       - on 200: add + close drawer
 *       - on 422: stay open + show Shopify message
 *    2) Hinzufügen & bezahlen
 *       - on 200 and 422: redirect to /cart
 * - Adds "Zum Produkt" overlay button at bottom-right of media/gallery
 * - Refreshes cart UI via Maestrooo events (best effort)
 *
 * MENGENSTAFFEL (eigene App):
 * Die Rabattstufen kommen aus dem JSON, das der Mengenstaffel-Block auf der
 * Produktseite ausgibt ([data-qb-config]). Die alten Koala-Leser bleiben als
 * Rueckfallebene erhalten, damit waehrend der Umstellung nichts ausfaellt.
 * Die Funktionsnamen mit "Koala" wurden bewusst beibehalten - sie werden an
 * mehreren Stellen aufgerufen, und ein Umbenennen brächte nur Fehlerquellen.
 */
console.log("[QB] custom.js loaded");
(() => {
    if (window.__QB_INITED__) return;
    window.__QB_INITED__ = true;
    const isMobile = () => window.matchMedia(`${window.themeVariables?.breakpoints?.["sm-max"] || "(max-width: 699px)"}`).matches;
    const ignoreSelector = ".product-card__quick-buy, form, button, input, select, textarea, label, summary, [role='button']";
    const normalizeUrl = (href) => new URL(href, window.location.origin).toString();
    const extractQuickBuyHtml = (html) => {
        const doc = new DOMParser().parseFromString(html, "text/html");
        const node = doc.querySelector("quick-buy-modal-content");
        return node ? node.innerHTML : doc.body?.innerHTML || html;
    };
    const fetchProductJson = async (handle) => {
        const res = await fetch(`/products/${encodeURIComponent(handle)}.js`, {
            credentials: "same-origin",
            headers: {
                Accept: "application/json"
            },
        });
        if (!res.ok) throw new Error(`Product JSON failed: ${res.status}`);
        return await res.json();
    };
    const getHandleFromProductUrl = (productUrl) => {
        const u = new URL(productUrl, window.location.origin);
        const m = u.pathname.match(/\/products\/([^\/]+)/);
        return m ? m[1] : null;
    };
    const getProductUrlFromCard = (card) => {
        const handle = card.getAttribute("handle");
        if (handle) return normalizeUrl(`/products/${handle}`);
        const a = card.querySelector(".product-card__figure a[href^='/products/']") || card.querySelector(".product-card__title a[href^='/products/']") || card.querySelector("a[href^='/products/']");
        const href = a?.getAttribute("href");
        if (!href) return null;
        const u = new URL(href, window.location.origin);
        u.search = "";
        return u.toString();
    };
    const detectCurrencyCode = () => window.themeVariables?.currencyCode || window.Shopify?.currency?.active || document.documentElement.getAttribute("data-shopify-currency") || "CHF";
    const formatMoneyCents = (cents) => {
        const currency = detectCurrencyCode();
        const amount = Number(cents || 0) / 100;
        try {
            return new Intl.NumberFormat(undefined, {
                style: "currency",
                currency
            }).format(amount);
        } catch (_) {
            return `${amount.toFixed(2)} ${currency}`;
        }
    };

    // --- Mengenstaffel / Rabattstufen ---
    const koalaDealCache = new Map();
    const htmlDecode = (s) => {
        const t = document.createElement("textarea");
        t.innerHTML = s || "";
        return t.value;
    };

    const parseKoalaNumber = (value) => {
        const match = String(value || "").match(/-?\d+(?:[.,]\d+)?/);
        if (!match) return NaN;
        return Number(match[0].replace(",", "."));
    };

    /**
     * Bevorzugte Quelle: das JSON, das unser Mengenstaffel-Block ausgibt.
     * Beispiel: {"productId":123,"tiers":[{"min":10,"percent":3}, ...]}
     */
    const readStaffelConfig = (doc) => {
        const node = doc?.querySelector("[data-qb-config]");
        if (!node) return null;

        try {
            const parsed = JSON.parse(node.textContent || "{}");
            const tiers = (Array.isArray(parsed.tiers) ? parsed.tiers : [])
                .map((t) => ({ qty: Number(t.min), pct: Number(t.percent) }))
                .filter((t) => Number.isFinite(t.qty) && t.qty > 0 && Number.isFinite(t.pct) && t.pct > 0)
                .sort((a, b) => a.qty - b.qty);

            if (!tiers.length) return null;

            return {
                definition: {
                    contracts: tiers.map((t, i) => {
                        const next = tiers[i + 1];
                        return {
                            minQuantity: t.qty,
                            maxQuantity: next ? next.qty - 1 : undefined,
                            discount: { type: "percentage", value: t.pct },
                        };
                    }),
                },
            };
        } catch (err) {
            console.warn("[QB] Staffel-JSON unlesbar:", err);
            return null;
        }
    };

    const buildKoalaDealFromRenderedMarkup = (doc) => {
        if (!doc) return null;

        const roots = Array.from(doc.querySelectorAll("koala-quantity-breaks-embed"));
        const scopes = roots.length ? roots : [doc];
        let bestTiers = [];

        scopes.forEach((scope) => {
            const tiers = Array.from(scope.querySelectorAll(".koala-deal__tiers__list-item"))
                .map((row) => {
                    const titleEl = row.querySelector(".koala-deal__tier__title");
                    const subtitleEl = row.querySelector(".koala-deal__tier__subtitle");
                    const title = titleEl?.getAttribute("data-template") || titleEl?.textContent || "";
                    const subtitle = subtitleEl?.getAttribute("data-template") || subtitleEl?.textContent || "";
                    const qty = parseKoalaNumber(title);
                    const pctMatch = String(subtitle).match(/(-?\d+(?:[.,]\d+)?)\s*%/);
                    const pct = pctMatch ? Number(pctMatch[1].replace(",", ".")) : 0;

                    return { qty, pct };
                })
                .filter((tier) => Number.isFinite(tier.qty) && tier.qty > 0 && Number.isFinite(tier.pct) && tier.pct > 0)
                .sort((a, b) => a.qty - b.qty);

            const uniqueTiers = Array.from(new Map(tiers.map((tier) => [tier.qty, tier])).values());
            if (uniqueTiers.length > bestTiers.length) bestTiers = uniqueTiers;
        });

        if (!bestTiers.length) return null;

        return {
            definition: {
                contracts: bestTiers.map((tier, index) => {
                    const nextTier = bestTiers[index + 1];
                    return {
                        minQuantity: tier.qty,
                        maxQuantity: nextTier ? nextTier.qty - 1 : undefined,
                        discount: {
                            type: "percentage",
                            value: tier.pct,
                        },
                    };
                }),
            },
        };
    };

    const getUsableLegacyKoalaDeal = (doc) => {
        const styleNodes = Array.from(doc?.querySelectorAll("koala-style[deal]") || []);

        for (const node of styleNodes) {
            try {
                const parsed = JSON.parse(htmlDecode(node.getAttribute("deal")));
                const deal = parsed?.deal || parsed;
                const contracts = deal?.definition?.contracts;
                const hasUsableContracts = Array.isArray(contracts) && contracts.some((contract) =>
                    Number.isFinite(Number(contract?.minQuantity)) &&
                    contract?.discount &&
                    Number.isFinite(Number(contract.discount.value))
                );
                if (hasUsableContracts) return deal;
            } catch (_) {}
        }

        return null;
    };

    const fetchKoalaDealFromProductPage = async (productUrl) => {
        try {
            const u = new URL(productUrl, window.location.origin);
            u.searchParams.delete("view"); // ensure full HTML
            const res = await fetch(u.toString(), {
                credentials: "same-origin",
                cache: "no-store",
            });
            if (!res.ok) return null;

            const html = await res.text();
            const doc = new DOMParser().parseFromString(html, "text/html");

            // Eigene Mengenstaffel hat Vorrang.
            const ownDeal = readStaffelConfig(doc);
            if (ownDeal) return ownDeal;

            // Rueckfallebene 1: gerendertes Koala-Widget.
            const renderedDeal = buildKoalaDealFromRenderedMarkup(doc);
            if (renderedDeal) return renderedDeal;

            // Rueckfallebene 2: aeltere Koala-Ausgabe in <koala-style deal="...">.
            const legacyDeal = getUsableLegacyKoalaDeal(doc);
            if (legacyDeal) return legacyDeal;

            console.warn("[QB] Keine Rabattstufen gefunden:", u.pathname);
            return null;
        } catch (err) {
            console.warn("[QB] Abruf der Rabattstufen fehlgeschlagen:", err);
            return null;
        }
    };

    const getKoalaDeal = async (productUrl, sectionHtml) => {
        if (!productUrl) return null;
        const key = normalizeUrl(productUrl);
        if (koalaDealCache.has(key)) return koalaDealCache.get(key);

        // Der Produktabschnitt wurde fuer den Drawer ohnehin schon geladen.
        // Steckt unser Block darin, sparen wir uns einen zweiten Seitenaufruf.
        let deal = null;
        if (sectionHtml) {
            deal = readStaffelConfig(new DOMParser().parseFromString(sectionHtml, "text/html"));
        }
        if (!deal) deal = await fetchKoalaDealFromProductPage(key);

        // Cache successes only so a transient request/app failure can recover on the next open.
        if (deal) koalaDealCache.set(key, deal);
        return deal;
    };

    const pickKoalaTierChoices = (deal) => {
        const contracts = deal?.definition?.contracts;
        if (!Array.isArray(contracts)) return [];

        // Alle positiven Stufen anzeigen - die Staffel bestimmt jetzt die App,
        // nicht eine fest eingebaute Liste.
        return contracts
            .filter((c) => c && c.discount && String(c.discount.type) === "percentage" && Number(c.discount.value) > 0)
            .map((c) => ({
                qty: Number(c.minQuantity),
                pct: Number(c.discount.value),
                max: c.maxQuantity === undefined ? null : Number(c.maxQuantity),
            }))
            .filter((t) => Number.isFinite(t.qty) && t.qty > 0 && Number.isFinite(t.pct));
    };

    const getKoalaDiscountPctForQty = (deal, qty) => {
        const contracts = deal?.definition?.contracts;
        if (!Array.isArray(contracts) || !Number.isFinite(qty)) return 0;
        const match = contracts.find((c) => {
            const min = Number(c?.minQuantity ?? 0);
            const max = c?.maxQuantity === undefined ? Infinity : Number(c.maxQuantity);
            return qty >= min && qty <= max;
        });
        const type = String(match?.discount?.type ?? "percentage");
        const pct = Number(match?.discount?.value ?? 0);
        if (type !== "percentage") return 0;
        return Number.isFinite(pct) ? pct : 0;
    };

    const calcDiscountedTotalCents = (basePriceCents, qty, pct) => {
  const q = Math.max(1, Number(qty) || 1);
  const p = Math.max(0, Number(pct) || 0);
  const base = Number(basePriceCents || 0);

  // 1) compute discounted unit price (rounded to cents)
  const unitCents = Math.round(base * (100 - p) / 100);

  // 2) total = unit price * quantity
  return unitCents * q;
};


    // Bottom-right overlay link on the product media/gallery
    const attachProductLinkBottomRight = (bodyEl, productUrl, linkText = "Zum Produkt") => {
        bodyEl.querySelectorAll("[data-qb-product-link]").forEach((n) => n.remove());
        if (!productUrl) return;
        const mediaHost = bodyEl.querySelector("media-gallery") || bodyEl.querySelector(".product__media") || bodyEl.querySelector(".product-media") || bodyEl.querySelector(".product__media-wrapper") || bodyEl.querySelector(".product__gallery") || bodyEl.querySelector(".product-gallery") || bodyEl.querySelector("img");
        if (!mediaHost) return;
        const wrapper = mediaHost.closest(".product__media-wrapper, .product__media, .product-media, .product__gallery, .product-gallery") || mediaHost.parentElement;
        if (!wrapper) return;
        try {
            if (getComputedStyle(wrapper).position === "static") wrapper.style.position = "relative";
        } catch (_) {}
        const a = document.createElement("a");
        a.setAttribute("data-qb-product-link", "");
        a.href = productUrl;
        a.textContent = linkText;
        a.style.position = "absolute";
        a.style.right = "10px";
        a.style.bottom = "10px";
        a.style.zIndex = "5";
        a.style.padding = "10px 12px";
        a.style.borderRadius = "999px";
        a.style.background = "rgba(255,255,255,0.9)";
        a.style.backdropFilter = "blur(6px)";
        a.style.textDecoration = "none";
        a.style.color = "inherit";
        a.style.fontWeight = "600";
        a.style.whiteSpace = "nowrap";
        a.style.boxShadow = "0 8px 20px rgba(0,0,0,0.12)";
        // Don't let it trigger quickbuy interceptors
        a.addEventListener("pointerdown", (e) => e.stopPropagation(), {
            capture: true
        });
        a.addEventListener("click", (e) => e.stopPropagation(), {
            capture: true
        });
        wrapper.appendChild(a);
    };
    // Impact/Maestrooo add-to-cart: FormData + sections + sections_url + X-Requested-With
    const addToCartImpact = async ({
        variantId,
        quantity,
        productId,
        sectionId
    }) => {
        const fd = new FormData();
        fd.append("quantity", String(quantity));
        fd.append("form_type", "product");
        fd.append("utf8", "✓");
        fd.append("id", String(variantId));
        fd.append("sections", "variant-added");
        fd.append("sections_url", `/variants/${variantId}`);
        if (productId) fd.append("product-id", String(productId));
        if (sectionId) fd.append("section-id", String(sectionId));
        const res = await fetch("/cart/add.js", {
            method: "POST",
            credentials: "same-origin",
            headers: {
                Accept: "application/json",
                "X-Requested-With": "XMLHttpRequest",
            },
            body: fd,
        });
        const data = await res.json().catch(() => ({}));
        return {
            status: res.status,
            ok: res.ok,
            data
        };
    };
    const getCart = async () => await (await fetch("/cart.js", {
        credentials: "same-origin"
    })).json();
    const maestroooRefreshCartUI = (cart, variantId, addedQty) => {
        document.documentElement.dispatchEvent(new CustomEvent("cart:refresh", {
            bubbles: true
        }));
        if (addedQty > 0) {
            const items = [{
                id: variantId,
                quantity: addedQty
            }];
            document.dispatchEvent(new CustomEvent("variant:add", {
                bubbles: true,
                detail: {
                    items,
                    cart
                }
            }));
            document.dispatchEvent(new CustomEvent("cart:change", {
                bubbles: true,
                detail: {
                    cart,
                    baseEvent: "variant:add"
                }
            }));
        }
    };
    async function fetchMetafieldsJson(currentProductUrl) {
        const u = new URL(currentProductUrl, window.location.origin);
        // Strip Shopify JSON endpoints if they sneak in
        u.pathname = u.pathname.replace(/(\.js|\.json)$/, "");
        // Force the view param (this is what maps to templates/product.qb-metafields.json.liquid)
        u.searchParams.set("view", "qb-metafields.json");
        console.log("[QB] metafields URL =>", u.toString());
        const res = await fetch(u.toString(), {
            credentials: "same-origin",
            cache: "no-store" // optional, but great for debugging
        });
        console.log("[QB] metafields status =>", res.status);
        const text = await res.text();
        // Debug: show what you actually got
        console.log("[QB] metafields response first 120 =>", text.slice(0, 120));
        try {
            return JSON.parse(text);
        } catch (e) {
            console.warn("[QB] NOT JSON (so not your view template).");
            return null;
        }
    }

    function getByPath(obj, path) {
        if (!obj || !path) return undefined;
        return path.split(".").reduce((acc, k) => (acc && acc[k] !== undefined ? acc[k] : undefined), obj);
    }

    function normalizeValue(val) {
        if (Array.isArray(val)) return val.filter(Boolean).join(", ");
        if (val === null || val === undefined) return "";
        return String(val).trim();
    }

    function formatMeta(val, unit) {
        const s = normalizeValue(val);
        if (!s) return "";
        if (!unit) return s;
        if (/[a-zA-Z]/.test(s) && s.toLowerCase().includes(unit.toLowerCase())) return s;
        if (/^[0-9]+([.,][0-9]+)?$/.test(s)) return `${s.replace(",", ".")} ${unit}`;
        return `${s} ${unit}`;
    }
    async function renderMetaIcons(productUrl, drawer) {
        const metaWrap = drawer.querySelector("[data-qb-metaicons]");
        if (!metaWrap) return;
        const items = Array.from(metaWrap.querySelectorAll("[data-qb-metaicon]"));
        metaWrap.style.display = "none";
        items.forEach((it) => (it.style.display = "none"));
        const mfRaw = await fetchMetafieldsJson(productUrl);
        const mf = mfRaw?.product ?? mfRaw;
        if (!mf) return;
        let shown = 0;
        items.forEach((item) => {
            const key = item.getAttribute("data-key") || "";
            const unit = item.getAttribute("data-unit") || "";
            const img = item.querySelector("img");
            const textEl = item.querySelector("[data-qb-metaicon-text]");
            let val = getByPath(mf, key);
            // fallback for your typo key
            if (!normalizeValue(val) && key === "custom.niktoin_mg_pro_ml") {
                val = getByPath(mf, "custom.nikotin_mg_pro_ml");
            }
            const txt = formatMeta(val, unit);
            // show only if BOTH icon + text exist
            if (!txt || !img || !img.getAttribute("src")) {
                if (textEl) textEl.textContent = "";
                item.style.display = "none";
                return;
            }
            if (textEl) textEl.textContent = txt;
            item.style.display = "";
            shown += 1;
        });
        metaWrap.style.display = shown > 0 ? "" : "none";
    }
    const init = async () => {
        // Wait for x-drawer definition if needed
        if (!isMobile()) {
            console.log("[QB] Desktop detected — quickbuy drawer disabled.");
            return;
        }
        if (!customElements.get("x-drawer")) {
            try {
                await Promise.race([
                    customElements.whenDefined("x-drawer"),
                    new Promise((r) => setTimeout(r, 2500)),
                ]);
            } catch (_) {}
        }
        const drawer = document.querySelector("[data-quickbuy-drawer]");
        if (!drawer) {
            console.warn("[QB] Drawer not found (data-quickbuy-drawer).");
            return;
        }
        const titleEl = drawer.querySelector("[data-quickbuy-title]");
        const bodyEl = drawer.querySelector("[data-quickbuy-body]");
        const vendorEl = drawer.querySelector("[data-quickbuy-vendor]");
        const descWrapEl = drawer.querySelector("[data-quickbuy-description-wrap]");
        const descEl = drawer.querySelector("[data-quickbuy-description]");
        const qtyInput = drawer.querySelector("[data-quickbuy-qty-input]");
        const addBtn = drawer.querySelector("[data-quickbuy-add]");
        let payBtn = drawer.querySelector("[data-quickbuy-pay]");
        const presetButtons = Array.from(drawer.querySelectorAll("[data-quickbuy-preset]"));
        const priceEl = drawer.querySelector("[data-quickbuy-price]");
        const compareEl = drawer.querySelector("[data-quickbuy-compare]");
        const discountEl = drawer.querySelector("[data-quickbuy-discount]");
        const totalPriceEl = drawer.querySelector("[data-qb-total-price]");
        const tierWrap = drawer.querySelector("[data-qb-tierpick]");
        const tierRow  = drawer.querySelector("[data-qb-tierpick-row]");
        // Create pay button if missing (same style as addBtn)
        if (!payBtn && addBtn) {
            payBtn = document.createElement("button");
            payBtn.type = "button";
            payBtn.setAttribute("data-quickbuy-pay", "");
            payBtn.className = addBtn.className;
            payBtn.style.opacity = "0.92";
            payBtn.textContent = "Hinzufügen & bezahlen";
            addBtn.insertAdjacentElement("afterend", payBtn);
            payBtn.style.marginTop = "8px";
        }
        // Create message lines if missing
        let msgEl = drawer.querySelector("[data-quickbuy-qty-message]");
        if (!msgEl && qtyInput) {
            msgEl = document.createElement("p");
            msgEl.setAttribute("data-quickbuy-qty-message", "");
            msgEl.className = "text-xs text-subdued";
            msgEl.style.marginTop = "6px";
            qtyInput.closest(".form-control")?.appendChild(msgEl);
        }
        let addedEl = drawer.querySelector("[data-quickbuy-added-message]");
        if (!addedEl && qtyInput) {
            addedEl = document.createElement("p");
            addedEl.setAttribute("data-quickbuy-added-message", "");
            addedEl.className = "text-xs";
            addedEl.style.marginTop = "6px";
            qtyInput.closest(".form-control")?.appendChild(addedEl);
        }
        if (!titleEl || !bodyEl || !descWrapEl || !descEl || !qtyInput || !addBtn || !payBtn) {
            console.warn("[QB] Missing required drawer elements.");
            return;
        }
        const sectionIdFetch = drawer.getAttribute("data-product-section-id") || "main-product";
        const uiMaxQty = (() => {
            const m = Number.parseInt(qtyInput.getAttribute("max") || "", 10);
            return Number.isFinite(m) && m > 0 ? m : null;
        })();
        const defaultAddText = addBtn.textContent.trim() || "In den Warenkorb";
        const defaultPayText = payBtn.textContent.trim() || "Hinzufügen & bezahlen";
        let isAvailable = true;
        let variantChangeHandler = null;
        const getCurrentVariantAvailable = (product) => {
            const vid = getSelectedVariantIdFromInjectedUi() || fallbackVariantId;
            if (product && Array.isArray(product.variants) && vid) {
                const v = product.variants.find((x) => Number(x.id) === Number(vid));
                if (v) return !!v.available;
            }
            return !!(product && product.available);
        };
        const applyAvailabilityState = (available) => {
            isAvailable = !!available;
            if (!isAvailable) {
                // SOLD OUT MODE
                addBtn.textContent = "Ausverkauft";
                addBtn.disabled = true;
                payBtn.textContent = "Bei Verfügbarkeit benachrichtigen";
                payBtn.disabled = false; // clickable later (for now: no-op)
                payBtn.classList.add("qb-notify");
                // Optional: make qty irrelevant when sold out
                qtyInput.value = "";
            } else {
                // NORMAL MODE
                addBtn.textContent = defaultAddText;
                payBtn.textContent = defaultPayText;
                payBtn.classList.remove("qb-notify");
                updateEnabled(); // re-apply qty logic
            }
        };
        let armedUrl = null;
        let inFlight = null;
        let lastOpenAt = 0;
        let fallbackVariantId = null;
        let currentProductUrl = null;
        let currentProduct = null;
        let currentKoalaDeal = null;
        const clearMessage = () => {
            if (msgEl) msgEl.textContent = "";
        };
        const showMessage = (txt) => {
            if (msgEl) msgEl.textContent = txt || "";
        };
        const clearAdded = () => {
            if (addedEl) addedEl.textContent = "";
        };
        const showAdded = (qty) => {
            if (!addedEl) return;
            addedEl.textContent = qty ? `Hinzugefügt: ${qty}` : "";
        };
        const clampQty = (raw) => {
            const n = Number.parseInt(String(raw), 10);
            if (!Number.isFinite(n) || n < 1) return null; // null = “not selected”
            if (uiMaxQty) return Math.min(n, uiMaxQty);
            return n;
        };
        const setQty = (raw) => {
            const v = clampQty(raw);
            qtyInput.value = v === null ? "" : String(v);
            qtyInput.dispatchEvent(new Event("input", {
                bubbles: true
            }));
            qtyInput.dispatchEvent(new Event("change", {
                bubbles: true
            }));
            updateEnabled();
        };
        const getQty = () => clampQty(qtyInput.value);
        const updateEnabled = () => {
            if (!isAvailable) {
                addBtn.disabled = true;
                payBtn.disabled = false; // notify button stays enabled
                return;
            }
            const ok = !!(getQty() && getQty() >= 1);
            addBtn.disabled = !ok;
            payBtn.disabled = !ok;
        };
        const setBusy = (busy) => {
            const ok = !!(getQty() && getQty() >= 1);
            addBtn.disabled = busy || !ok;
            payBtn.disabled = busy || !ok;
            if (busy) {
                addBtn.setAttribute("aria-busy", "true");
                payBtn.setAttribute("aria-busy", "true");
            } else {
                addBtn.removeAttribute("aria-busy");
                payBtn.removeAttribute("aria-busy");
            }
            // Keep texts constant (but don't override sold-out UI)
            if (isAvailable) {
                addBtn.textContent = defaultAddText;
                payBtn.textContent = defaultPayText;
            } else {
                addBtn.textContent = "Ausverkauft";
                payBtn.textContent = "Bei Verfügbarkeit benachrichtigen";
            }
        };
        const openDrawer = async () => {
            if (typeof drawer.show === "function") await drawer.show();
            else drawer.setAttribute("open", "");
        };
        const renderPrice = (product) => {
            if (!priceEl) return;
            const price = Number(product?.price ?? 0);
            const compare = Number(product?.compare_at_price ?? 0);
            priceEl.textContent = formatMoneyCents(price);
            if (compareEl) {
                compareEl.textContent = "";
                compareEl.style.display = "none";
            }
            if (discountEl) {
                discountEl.textContent = "";
                discountEl.style.display = "none";
            }
            if (compare && compare > price) {
                if (compareEl) {
                    compareEl.textContent = formatMoneyCents(compare);
                    compareEl.style.display = "";
                }
                if (discountEl) {
                    const save = compare - price;
                    const pct = Math.round((save / compare) * 100);
                    discountEl.textContent = `Spare ${formatMoneyCents(save)} (${pct}%)`;
                    discountEl.style.display = "";
                }
            }
        };
        const setLoading = () => {
            titleEl.textContent = "…";
            if (vendorEl) vendorEl.textContent = "";
            clearMessage();
            clearAdded();
            bodyEl.innerHTML = `<div class="text-subdued">Loading…</div>`;
            if (tierWrap && tierRow) { tierWrap.hidden = true; tierRow.innerHTML = ""; }
            if (priceEl) priceEl.textContent = "";
            if (totalPriceEl) totalPriceEl.textContent = "";
            if (compareEl) compareEl.style.display = "none";
            if (discountEl) discountEl.style.display = "none";
            descWrapEl.open = false;
            descEl.innerHTML = "";
            descWrapEl.style.display = "none";
            // Not preselected
            setQty("");
            setBusy(true);
            fallbackVariantId = null;
            currentProductUrl = null;
            currentProduct = null;
            currentKoalaDeal = null;
        };
        const getSelectedVariantIdFromInjectedUi = () => {
            const idInput = bodyEl.querySelector('form[action^="/cart/add"] input[name="id"]') || bodyEl.querySelector('input[name="id"]');
            const v = idInput?.value ? Number.parseInt(idInput.value, 10) : NaN;
            return Number.isFinite(v) ? v : null;
        };
        const getThemeAddMetaFromInjectedForm = () => {
            const form = bodyEl.querySelector('form[action^="/cart/add"]');
            if (!form) return {
                productId: null,
                sectionId: null
            };
            const productId = form.querySelector('input[name="product-id"]')?.value || null;
            const sectionId = form.querySelector('input[name="section-id"]')?.value || null;
            return {
                productId,
                sectionId
            };
        };

        const getSelectedVariantPriceCents = (product) => {
            const vid = getSelectedVariantIdFromInjectedUi() || fallbackVariantId;
            if (product && Array.isArray(product.variants) && vid) {
                const v = product.variants.find((x) => Number(x.id) === Number(vid));
                const cents = Number(v?.price ?? product?.price ?? 0);
                return Number.isFinite(cents) ? cents : 0;
            }
            const cents = Number(product?.price ?? 0);
            return Number.isFinite(cents) ? cents : 0;
        };

        const formatUnitPerStk = (totalCents, qty) => {
            const currency = detectCurrencyCode();
            const unit = qty ? (Number(totalCents) / Number(qty)) / 100 : 0;
            const number = new Intl.NumberFormat(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(unit || 0);
            return `${number} ${currency}/Stk`;
        };

        const setTierActiveFromQty = () => {
            if (!tierRow) return;
            const qty = getQty();
            tierRow.querySelectorAll(".qb-tierbox").forEach((b) => {
                const q = Number(b.getAttribute("data-qty"));
                b.classList.toggle("is-active", !!qty && q === qty);
                b.setAttribute("aria-pressed", (!!qty && q === qty) ? "true" : "false");
            });
        };

        const updateTierUnitPrices = (product) => {
            if (!tierRow) return;
            const baseCents = getSelectedVariantPriceCents(product);
            tierRow.querySelectorAll(".qb-tierbox").forEach((b) => {
                const qty = Number(b.getAttribute("data-qty"));
                const pct = Number(b.getAttribute("data-pct"));
                if (!Number.isFinite(qty) || !qty) return;

                const total = calcDiscountedTotalCents(baseCents, qty, pct);
                const unit = b.querySelector("[data-qb-tier-unit]");
                if (unit) unit.textContent = formatUnitPerStk(total, qty);
            });
        };

        const renderTierPicker = (deal, product) => {
            if (!tierWrap || !tierRow) return;
            tierRow.innerHTML = "";

            const choices = pickKoalaTierChoices(deal);
            if (!choices.length) {
                tierWrap.hidden = true;
                return;
            }

            tierWrap.hidden = false;

            choices.forEach((t) => {
                const btn = document.createElement("button");
                btn.type = "button";
                btn.className = "qb-tierbox";
                btn.setAttribute("data-qty", String(t.qty));
                btn.setAttribute("data-pct", String(t.pct));
                btn.setAttribute("aria-pressed", "false");
                btn.innerHTML = `
                    <div class="qb-tierbox__qty">${t.qty} Stk</div>
                    <div class="qb-tierbox__pct">${t.pct}% Rabatt</div>
                    <div class="qb-tierbox__unit" data-qb-tier-unit></div>
                `;

                btn.addEventListener("click", () => {
                    clearMessage();
                    clearAdded();
                    setQty(t.qty);
                    // clampAfterThemeStep will call updateTotalPrice + setTierActiveFromQty
                });

                tierRow.appendChild(btn);
            });

            updateTierUnitPrices(product);
            setTierActiveFromQty();
        };

        const updateTotalPrice = () => {
            if (!totalPriceEl) return;
            if (!currentProduct) { totalPriceEl.textContent = ""; return; }

            const qty = getQty();
            if (!qty) { totalPriceEl.textContent = ""; return; }

            const baseCents = getSelectedVariantPriceCents(currentProduct);
            const pct = getKoalaDiscountPctForQty(currentKoalaDeal, qty);
            const totalCents = calcDiscountedTotalCents(baseCents, qty, pct);
            totalPriceEl.textContent = formatMoneyCents(totalCents);
        };

        const openQuickBuy = async (productUrl) => {
            const now = Date.now();
            if (now - lastOpenAt < 200) return;
            lastOpenAt = now;
            if (inFlight) return inFlight;
            setLoading();
            await openDrawer();
            inFlight = (async () => {
                try {
                    const handle = getHandleFromProductUrl(productUrl);
                    if (!handle) throw new Error("Missing product handle");
                    const sectionUrl = new URL(productUrl, window.location.origin);
                    sectionUrl.searchParams.set("section_id", sectionIdFetch);
                    const [product, sectionRes] = await Promise.all([
                        fetchProductJson(handle),
                        fetch(sectionUrl.toString(), {
                            credentials: "same-origin"
                        }),
                    ]);
                    if (!sectionRes.ok) throw new Error(`Section fetch failed: ${sectionRes.status}`);
                    const sectionHtml = await sectionRes.text();
                    currentProductUrl = product?.url ? normalizeUrl(product.url) : normalizeUrl(`/products/${handle}`);
                    currentProduct = product;
                    titleEl.textContent = product?.title || "Quick buy";
                    if (vendorEl) vendorEl.textContent = product?.vendor ? String(product.vendor) : "";
                    renderPrice(product);
                    const descHtml = (product?.description || "").trim();
                    descEl.innerHTML = descHtml;
                    descWrapEl.open = false;
                    descWrapEl.style.display = descHtml ? "" : "none";
                    bodyEl.innerHTML = extractQuickBuyHtml(sectionHtml);
                    // bottom-right "Zum Produkt"
                    attachProductLinkBottomRight(bodyEl, currentProductUrl, "Zum Produkt");
                    await renderMetaIcons(currentProductUrl, drawer);
                    currentKoalaDeal = await getKoalaDeal(currentProductUrl, sectionHtml);
                    renderTierPicker(currentKoalaDeal, currentProduct);
                    updateTotalPrice();
                    fallbackVariantId = Array.isArray(product?.variants) && product.variants.length ? product.variants[0].id : null;
                    setQty("");
                    setBusy(false);
                    clearMessage();
                    clearAdded();
                    // apply availability state (product + selected variant)
                    applyAvailabilityState(getCurrentVariantAvailable(product));
                    updateTotalPrice();
                    // update availability when variant changes in injected UI
                    if (variantChangeHandler) bodyEl.removeEventListener("change", variantChangeHandler, true);
                    variantChangeHandler = () => {
                        applyAvailabilityState(getCurrentVariantAvailable(product));
                        updateTierUnitPrices(currentProduct);
                        updateTotalPrice();
                        setTierActiveFromQty();
                    };
                    bodyEl.addEventListener("change", variantChangeHandler, true);
                } finally {
                    inFlight = null;
                }
            })();
            return inFlight;
        };
        // Presets set quantity
        presetButtons.forEach((btn) => {
            btn.addEventListener("click", () => {
                clearMessage();
                clearAdded();
                setQty(btn.getAttribute("data-quickbuy-preset"));
            });
        });
        // IMPORTANT: do NOT handle +/- clicks (theme handles it); only clamp after input changes
        const clampAfterThemeStep = () => {
            queueMicrotask(() => {
                const v = clampQty(qtyInput.value);
                if (v === null) {
                    qtyInput.value = "";
                } else if (String(v) !== String(qtyInput.value)) {
                    qtyInput.value = String(v);
                    qtyInput.dispatchEvent(new Event("input", {
                        bubbles: true
                    }));
                }
                updateEnabled();
                updateTotalPrice();
                setTierActiveFromQty();
            });
        };
        qtyInput.addEventListener("input", () => {
            clearMessage();
            clearAdded();
            clampAfterThemeStep();
        });
        qtyInput.addEventListener("change", () => {
            clearMessage();
            clearAdded();
            clampAfterThemeStep();
        });
        /**
         * New rules:
         * - mode "add":
         *    - 200 => add + close drawer
         *    - 422 => stay open + show message
         * - mode "pay":
         *    - 200 => redirect /cart
         *    - 422 => redirect /cart
         */
        const doAdd = async ({
            mode
        }) => {
            clearMessage();
            clearAdded();
            const qty = getQty();
            if (!qty) return;
            const variantId = getSelectedVariantIdFromInjectedUi() || fallbackVariantId;
            if (!variantId) throw new Error("No variant selected");
            const beforeCart = await getCart();
            const beforeQty = beforeCart.items.find((i) => i.variant_id === variantId)?.quantity || 0;
            setBusy(true);
            const {
                productId,
                sectionId
            } = getThemeAddMetaFromInjectedForm();
            const result = await addToCartImpact({
                variantId,
                quantity: qty,
                productId,
                sectionId,
            });
            // Refresh cart + compute actually added (best effort)
            let afterCart = null;
            let actuallyAdded = 0;
            try {
                afterCart = await getCart();
                const afterQty = afterCart.items.find((i) => i.variant_id === variantId)?.quantity || 0;
                actuallyAdded = Math.max(0, afterQty - beforeQty);
                maestroooRefreshCartUI(afterCart, variantId, actuallyAdded);
            } catch (_) {}
            const msg = result?.data?.message || result?.data?.description || (result.status === 422 ? "Aufgrund der Verfügbarkeit wurde die Menge angepasst." : "");
            // ---- 422 handling ----
            if (result.status === 422) {
                if (mode === "add") {
                    // stay open + show only the sentence
                    if (msg) showMessage(msg);
                    setBusy(false);
                    return;
                }
                // mode === "pay" => redirect even on 422
                setBusy(false);
                window.location.href = "/cart";
                return;
            }
            // ---- non-200 errors ----
            if (result.status !== 200) {
                if (msg) showMessage(msg);
                setBusy(false);
                return;
            }
            // ---- 200 success ----
            // Show "Hinzugefügt: X" only for normal add (optional)
            if (mode === "add" && actuallyAdded > 0) showAdded(actuallyAdded);
            setBusy(false);
            if (mode === "pay") {
                window.location.href = "/cart";
                return;
            }
            // mode === "add" => close drawer
            if (typeof drawer.hide === "function") {
                await drawer.hide();
            }
        };
        addBtn.addEventListener("click", async () => {
            if (!isAvailable) return;
            try {
                await doAdd({
                    mode: "add"
                });
            } catch (err) {
                console.error("[QB] add error", err);
                setBusy(false);
                showMessage(err?.message || "Konnte nicht zum Warenkorb hinzufügen.");
            }
        });
payBtn.addEventListener("click", async () => {
  // SOLD OUT: behave like "Zum Produkt" (redirect to product page)
  if (!isAvailable) {
    if (currentProductUrl) window.location.href = currentProductUrl;
    return;
  }

  // NORMAL: pay flow
  try {
    await doAdd({ mode: "pay" });
  } catch (err) {
    console.error("[QB] pay error", err);
    setBusy(false);
    // pay button: if something throws before redirect, show message
    showMessage(err?.message || "Konnte nicht zum Warenkorb hinzufügen.");
  }
});

        // Intercept product-card clicks to open drawer (no redirects)
        function interceptPointerdown(e) {
            if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
            const card = e.target.closest("product-card");
            if (!card) return;
            if (e.target.closest(ignoreSelector)) return;
            const url = getProductUrlFromCard(card);
            if (!url) return;
            const link = e.target.closest("a[href^='/products/']");
            if (link?.hasAttribute("data-instant")) link.removeAttribute("data-instant");
            armedUrl = url;
            e.stopPropagation();
            e.stopImmediatePropagation?.();
        }

        function interceptClick(e) {
            if (e.target.closest("[data-qb-product-link]")) return;
            if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
            const card = e.target.closest("product-card");
            if (!card) return;
            if (e.target.closest(ignoreSelector)) return;
            const url = getProductUrlFromCard(card);
            if (!url) return;
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation?.();
            const finalUrl = armedUrl || url;
            armedUrl = null;
            openQuickBuy(finalUrl).catch((err) => console.error("[QB] openQuickBuy error", err));
        }
        document.addEventListener("pointerdown", interceptPointerdown, true);
        document.addEventListener("click", interceptClick, true);
        // initial state
        setQty("");
        updateEnabled();
        setBusy(false);
        console.log("[QB] quickbuy drawer ready");
    };
    if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", init, {
            once: true
        });
    } else {
        init();
    }
})();